import type {
	GroupedParametrics,
	ParametricListInfo,
	ParametricListValue,
	ParametricListValuewithCount,
	Pricelist,
	Product,
	ProductItem,
	SimplifiedProductRelations
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { defineStore, acceptHMRUpdate } from "pinia";

interface ProductQtyBreakPrice {
	Value: number;
	ValueIncVat: number;
	VatRate: number;
	PriceListId: number;
	IsBuyable: boolean;
	RecommendedQuantity: number | null;
	IsRecommendedQuantityFixed: boolean;
	Standard: number | null;
	Recommended: number | null;
	Catalog: number | null;
}

export interface ProductQtyBreak {
	PartNo: string;
	QtyBreak: number;
	ProductPrice: ProductQtyBreakPrice;
}

/**
 * The parts of a price list the storefront reads: which id to quote, whether
 * Norce treats it as publicly quotable, which market it prices for, when a
 * campaign ends and its campaign text.
 */
export type PriceListSummary = Pick<
	Pricelist,
	"Id" | "IsPublic" | "IsPrimary" | "SalesAreaId" | "EndDate" | "Description"
>;

interface State {
	selectedColor: ParametricListValue | ParametricListValuewithCount | null;
	selectedSecondParametric: ParametricListValue | undefined;
	products: Product | null;
	selectedProduct: Product | undefined;
	parametrics: GroupedParametrics;
	parametricsInformation: ParametricListInfo[];
	defaultProductImage: string;
	/** Every price list the client has — see `setPriceLists`. */
	allPriceLists: PriceListSummary[];
	relations: SimplifiedProductRelations[];
	lowestPrice: number | null;
	productType: string | null;
	bundleProducts: ProductItem[];
	/** Item pool of a multi-pack product, in catalogue order. */
	multiPackProducts: ProductItem[];
	/** Variant family ("Flera varianter") of the product, in catalogue order. */
	variantFamilyProducts: ProductItem[];
	// Qty breaks per variant of the currently loaded parent product.
	// Cleared whenever addProducts() sees a different parent product Id.
	quantityBreaksByVariant: Record<number, ProductQtyBreak[]>;
}
export const useProductStore = defineStore({
	id: "productStore",
	state: (): State => {
		return {
			selectedColor: null,
			selectedSecondParametric: undefined,
			products: null,
			selectedProduct: undefined,
			parametrics: {},
			parametricsInformation: [],
			defaultProductImage: "",
			allPriceLists: [],
			relations: [],
			lowestPrice: null,
			productType: null,
			bundleProducts: [],
			multiPackProducts: [],
			variantFamilyProducts: [],
			quantityBreaksByVariant: {}
		};
	},
	actions: {
		/**
		 * Keeps every list the client has, narrowed to the fields the storefront
		 * reads. Which of them apply is decided by the `priceLists` getter, not
		 * here, so the selection follows the shopper's sales area as it changes
		 * instead of being frozen at app init — the country is only chosen at
		 * checkout, long after this runs.
		 *
		 * Stored slim because the full records run to 70 kB of JSON for a client
		 * with a price list per country, and all of it would ride in the SSR
		 * payload of every page.
		 */
		setPriceLists(priceLists: Pricelist[]) {
			this.allPriceLists = priceLists.map(
				({ Id, IsPublic, IsPrimary, SalesAreaId, EndDate, Description }) => ({
					Id,
					IsPublic,
					IsPrimary,
					SalesAreaId,
					EndDate,
					Description
				})
			);
		},
		addSelectedColor(
			value: ParametricListValue | ParametricListValuewithCount
		) {
			this.selectedColor = value;
		},
		addSelectedSecondParametric(value: ParametricListValue | undefined) {
			this.selectedSecondParametric = value;
		},
		addProducts(value: Product) {
			if (this.products?.Id !== value.Id) {
				this.quantityBreaksByVariant = {};
			}
			this.products = value;
		},
		addSelectedProduct(value: Product | undefined) {
			this.selectedProduct = value;
		},
		addParametrics(value: GroupedParametrics) {
			this.parametrics = value;
		},
		addRelations(value: SimplifiedProductRelations[]) {
			this.relations = value;
		},
		addLowestPrice(value: number | null) {
			this.lowestPrice = value;
		},
		async addParametricsInformation(value: ParametricListInfo[]) {
			this.parametricsInformation = value;
		},
		async addDefaultProductImage(value: string) {
			this.defaultProductImage = value;
		},
		setProductType(value: string | null) {
			this.productType = value;
		},
		addBundleProducts(value: ProductItem[]) {
			this.bundleProducts = value;
		},
		addMultiPackProducts(value: ProductItem[]) {
			this.multiPackProducts = value;
		},
		addVariantFamilyProducts(value: ProductItem[]) {
			this.variantFamilyProducts = value;
		},
		setQuantityBreaksForVariant(variantId: number, value: ProductQtyBreak[]) {
			this.quantityBreaksByVariant = {
				...this.quantityBreaksByVariant,
				[variantId]: value
			};
		},
		clearQuantityBreaks() {
			this.quantityBreaksByVariant = {};
		}
	},
	getters: {
		/**
		 * The price lists that apply to the shopper, which is what every consumer
		 * means by "the price lists".
		 *
		 * One rule, the same one the request seed is built from: the lists priced
		 * for the shopper's sales area — their country's once one is chosen, the
		 * application's default until then — plus the lists a signed-in shopper
		 * and their company negotiated. Applied here rather than read back off the
		 * seed, so neither store waits on the other.
		 *
		 * A list with no area is left out. In Norce that means the application's
		 * default area, and on a multi-country application the default carries no
		 * VAT, so quoting from one shows a price the basket will not honour.
		 *
		 * There is no fallback for a market whose lists carry no area either. That
		 * is a configuration gap, and quoting it from another market's lists would
		 * hide it behind prices the basket will not honour. It has to be visible.
		 * (With this empty no seed is sent and Norce prices from the public lists;
		 * search prices its index rows the same way, `hitPriceListIds` — the
		 * application's own public lists, never another market's.)
		 *
		 * Deliberately unsorted: the seed sorts, because a cache key has to spell
		 * one set one way, but a dozen consumers read `priceLists[0]` and changing
		 * what lands there is a separate question from which lists apply.
		 *
		 * A getter rather than a filter applied when the lists arrive: the country
		 * is chosen at checkout, long after app init, and the selection has to
		 * follow it.
		 */
		priceLists(): PriceListSummary[] {
			const norceStore = useNorceStore();
			const areaId = norceStore.resolvedSalesAreaId;
			const negotiated = new Set(norceStore.negotiatedPriceListIds);

			return this.allPriceLists.filter(
				(priceList) =>
					(areaId !== undefined && priceList.SalesAreaId === areaId) ||
					negotiated.has(priceList.Id)
			);
		},
		getSelectedColor: (state) => state.selectedColor,
		getSelectedSecondParametric: (state) => state.selectedSecondParametric,
		getProducts: (state) => state.products,
		getSelectedProduct: (state) => state.selectedProduct,
		getParametrics: (state) => state.parametrics,
		getParametricsInformation: (state) => state.parametricsInformation,
		getDefaultProductImage: (state) => state.defaultProductImage,
		getRelations: (state) => state.relations,
		getLowestPrice: (state) => state.lowestPrice,
		// Qty breaks for the currently selected variant, resolved from the per-variant cache.
		quantityBreaks: (state): ProductQtyBreak[] =>
			state.selectedProduct?.Id !== undefined
				? (state.quantityBreaksByVariant[state.selectedProduct.Id] ?? [])
				: [],
		getQuantityBreaks(): ProductQtyBreak[] {
			return this.quantityBreaks;
		},
		getParentFlagIds: (state): string[] => {
			const seed = state.products?.FlagIdSeed;
			if (!seed) return [];
			return seed
				.split(",")
				.map((id) => id.trim())
				.filter(Boolean);
		}
	}
});
if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useProductStore, import.meta.hot));
}
